import * as Sentry from '@sentry/browser'
import React, { ReactNode, useState } from 'react'
import { FfpDashboard } from '@containers/FfpDashboard'
import { OrganizationDashboard } from '@containers/OrganizationDashboard'
import { track } from '@utils/tracking'
import styles from './experience.module.sass'

const STORAGE_KEY = 'ffp_dashboard_experience'

const readChoice = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'new'
  } catch {
    return false
  }
}

const saveChoice = (useNew: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, useNew ? 'new' : 'old')
  } catch {
    // Storage blocked (private window): the choice lasts until the page reloads.
  }
}

type DashboardProps = {
  organization: { id: number; name?: string }
  initialData: unknown
  incognito?: boolean
}

type Props = DashboardProps & {
  ffp_dashboard?: boolean
}

// The new dashboard is offered next to the current one while it is tried out.
// Without the flag this is exactly the current dashboard.
export const OrganizationDashboardExperience = ({ ffp_dashboard = false, ...dashboardProps }: Props) => {
  if (!ffp_dashboard) return <OrganizationDashboard {...dashboardProps} />

  return <ExperienceSwitch {...dashboardProps} />
}

const ExperienceSwitch = ({ organization, initialData, incognito }: DashboardProps) => {
  const [useNew, setUseNew] = useState(readChoice)
  const [fellBack, setFellBack] = useState(false)

  const onToggle = () => {
    const next = !useNew
    setUseNew(next)
    saveChoice(next)
    setFellBack(false)
    track('FFP Dashboard: Switch experience', { new_dashboard: next })
  }

  // The new dashboard broke: go back to the current one and forget the choice,
  // so a reload doesn't land on the broken page again.
  const onNewDashboardError = () => {
    setUseNew(false)
    saveChoice(false)
    setFellBack(true)
    track('FFP Dashboard: Fell back to current dashboard')
  }

  const dashboard = useNew
    ? <FallBackOnError onError={onNewDashboardError}><FfpDashboard organization={organization} incognito={incognito} /></FallBackOnError>
    : <OrganizationDashboard organization={organization} initialData={initialData} incognito={incognito} />

  return (
    <div className={styles.experience}>
      <div className={styles.bar}>
        <FallBackNotice show={fellBack} />
        <button type="button" role="switch" aria-checked={useNew} className={styles.switch} onClick={onToggle}>
          <span className={styles.track} aria-hidden="true">
            <span className={styles.thumb} />
          </span>
          Try new dashboard experience
        </button>
      </div>
      {dashboard}
    </div>
  )
}

const FallBackNotice = ({ show }: { show: boolean }) => {
  if (!show) return null

  return <p className={styles.notice} role="status">The new dashboard hit a problem, so we've brought back the current one.</p>
}

type BoundaryProps = { onError: () => void; children: ReactNode }

// React only catches a render error in a class component. Without this, an
// error anywhere in the new dashboard unmounts the switch with it, and the
// saved choice would bring the broken page back on every reload.
class FallBackOnError extends React.Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error, { tags: { reactComponent: 'FfpDashboard' } })
    this.props.onError()
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}
